import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { useUser } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import * as bootMetrics from '@/lib/boot/bootMetrics';

interface CurrentUserEntityResult {
  /** The current user's entity ID, or null if not available */
  entityId: string | null;
  /** The current org ID as a string, or null if no org selected */
  orgId: string | null;
  /** The current user's ID, or null if not logged in */
  userId: number | null;
  /** Whether the org users are still loading */
  isLoading: boolean;
}

/**
 * Hook to get the current user's entity ID within the current org.
 *
 * Reads `currentOrg.currentUserMembership.entityId` as the primary source
 * (set by the slim TeamSerializer). Falls back to scanning the `members` array
 * for backward compatibility with stale localStorage caches written before the
 * slim TeamSerializer backend deploy.
 *
 * @returns Object with entityId, orgId, userId, and isLoading state
 *
 * @example
 * const { entityId, orgId, isLoading } = useCurrentUserEntity();
 * if (entityId && orgId) {
 *   // Ready to fetch timeline events
 * }
 */
export function useCurrentUserEntity(): CurrentUserEntityResult {
  const { currentOrg, isLoading } = useOrg();
  const user = useUser();
  const queryClient = useQueryClient();

  const membershipEntityId =
    currentOrg?.currentUserMembership?.entityId ?? null;
  const fallbackMembers = currentOrg?.members;

  const entityId = useMemo(() => {
    const userId = user?.id;
    if (userId === undefined || userId === null) {
      return null;
    }
    // Primary: read from new currentUserMembership field on Team.
    if (membershipEntityId) {
      return membershipEntityId;
    }
    // Fallback: scan legacy members array (handles stale caches from
    // before the slim TeamSerializer backend deploy).
    if (!fallbackMembers || fallbackMembers.length === 0) {
      return null;
    }
    const currentMember = fallbackMembers.find(
      member => member.userId === userId
    );
    return currentMember?.entityId ?? null;
  }, [user?.id, membershipEntityId, fallbackMembers]);

  const bootMarkRef = useRef(false);
  useEffect(() => {
    if (bootMarkRef.current) {
      return;
    }
    if (isLoading || !user?.id) {
      return;
    }
    bootMarkRef.current = true;
    bootMetrics.cacheCheck('currentUserEntity', queryClient, [
      'availableOrgs',
      user.id,
    ]);
  }, [isLoading, entityId, queryClient, user]);

  return {
    entityId,
    orgId: currentOrg?.id?.toString() ?? null,
    userId: user?.id ?? null,
    isLoading,
  };
}
